import { apiClient, ApiResponse } from './client';

export interface FlipOpportunity {
  seller_address: string;
  collection_id: string;
  collection_name: string;
  collection_slug: string | null;
  collection_creator_address: string;
  collection_taxon: number;
  discount_percentage: number;
  xrp_below_floor: number;
  sale_price: number;
  floor_price_at_sale: number;
  occurred_at: string;
  remaining_nft_count: number;
  nft_id: string;
  nft_name: string | null;
  nft_thumbnail_url: string | null;
  collection_volume_24h: number | null;
  collection_volume_7d: number | null;
  collection_volume_30d: number | null;
}

export interface FlipsFilters {
  days: number;
  minDiscountPercent: number;
  minXrpDifference: number;
  minSalePrice: number;
}

export interface FlipsData {
  opportunities: FlipOpportunity[];
  isAuthenticated: boolean;
  filters: FlipsFilters;
}

export type FlipsResponse = ApiResponse<FlipsData>;

export interface GetFlipsOptions {
  days?: number;
  minDiscountPercent?: number;
  minXrpDifference?: number;
  minSalePrice?: number;
}

export async function getFlipOpportunities(options: GetFlipsOptions = {}): Promise<FlipsResponse> {
  const params = new URLSearchParams();

  if (options.days !== undefined) {
    params.append('days', options.days.toString());
  }
  if (options.minDiscountPercent !== undefined) {
    params.append('minDiscountPercent', options.minDiscountPercent.toString());
  }
  if (options.minXrpDifference !== undefined) {
    params.append('minXrpDifference', options.minXrpDifference.toString());
  }
  if (options.minSalePrice !== undefined) {
    params.append('minSalePrice', options.minSalePrice.toString());
  }

  const queryString = params.toString();
  const url = queryString ? `/flip?${queryString}` : '/flip';

  return apiClient.get<FlipsResponse>(url);
}

export interface WalletFlipOpportunity {
  discount_percentage: number;
  sale_price: number;
  floor_price_at_sale: number;
  occurred_at: string;
  nft_id: string;
  nft_name: string | null;
  current_floor_price: number | null;
  suggested_offer_low: number | null;
  suggested_offer_high: number | null;
}

export type WalletFlipOpportunityResponse = ApiResponse<WalletFlipOpportunity | null>;

/**
 * Check if a wallet has a flip opportunity for a specific collection.
 * Returns the highest discount sale and suggested offer range.
 */
export async function getWalletFlipOpportunity(
  walletAddress: string,
  collectionId: string
): Promise<WalletFlipOpportunityResponse> {
  return apiClient.get<WalletFlipOpportunityResponse>(
    `/flip/wallet/${walletAddress}/collection/${encodeURIComponent(collectionId)}`
  );
}

/**
 * Check if the current owner of an NFT has a flip opportunity for that collection.
 * Returns the highest discount sale and suggested offer range.
 */
export async function getNFTFlipOpportunity(
  nftId: string
): Promise<WalletFlipOpportunityResponse> {
  return apiClient.get<WalletFlipOpportunityResponse>(
    `/flip/nft/${encodeURIComponent(nftId)}`
  );
}
